import ActionButton from '@repo/ui/action-button'
import Button from '@repo/ui/button'
import { Column, TableRequiredFields } from '@repo/ui/data-table'
import { DeleteIcon, PenIcon } from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import React, { RefObject, useState } from 'react'

import { restApiClient } from '../../../api/rest-api-client'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../lib/hooks/use-translate'
import { encodeNestedObject } from '../../../lib/utils/functions/misc.functions'
import { TABLE_ACTION_BUTTONS_CLASSNAME } from '../../../utils/constants/table-buttons-class.constants'
import ActionTableWrapper from '../action-table-wrapper'
import DynamicDataTable, { DynamicDataTableProps, DynamicTableRef } from '../dynamic-table'
import { getDeleteMessage } from '../dynamic-table/utils/deleteMessage'

// Generic type Props with explicit constraint for TData and TFilter
type Props<
  TData,
  TFilter,
  TExpanded extends TableRequiredFields | undefined = undefined
> = DynamicDataTableProps<TData, TFilter, TExpanded> & {
  widget?: React.ReactNode | ((data: TData[]) => React.ReactNode)
  variant?: 'default' | 'action'
  actionTableLabel?: React.ReactNode
  permissions?: (row: TData) => {
    delete?: boolean
    edit?: boolean
  }
  onEditAction?: (row: TData) => void
  onDelete?: () => void
}

// Forward Ref with generic type arguments at the function level
const NestedDataTable = React.forwardRef(
  <TData extends TableRequiredFields, TFilter>(
    {
      onEditAction,
      variant = 'default',
      isSearchable = false,
      permissions = (_) => ({ delete: true, edit: true }),
      ...props
    }: Props<TData, TFilter>,
    ref: React.Ref<DynamicTableRef<TData>>
  ) => {
    const [loading, setLoading] = useState(false)
    const { t } = useTranslate()

    // Using custom hook for modal management
    const { toggleModal, modalState, selectedData } = useModalManager<'delete' | 'edit', TData>([
      'delete',
      'edit'
    ])

    // Add custom columns including actions for editing and deleting
    const columns: Column<TData>[] = [
      ...props.columns,
      {
        accessorKey: 'id',
        header: '',
        cell: ({ row }) => (
          <div className={TABLE_ACTION_BUTTONS_CLASSNAME}>
            {onEditAction && permissions(row.original).edit && (
              <ActionButton
                variant={'grey'}
                icon={PenIcon}
                onClick={(e) => {
                  e.stopPropagation()
                  onEditAction(row.original)
                }}
              />
            )}
            {permissions(row.original).delete && (
              <ActionButton
                variant={'danger'}
                icon={DeleteIcon}
                onClick={(e) => {
                  e.stopPropagation()
                  toggleModal('delete', row.original)
                }}
              />
            )}
          </div>
        )
      }
    ]

    // Helper function to refetch data if ref is an object ref
    const refetchTableData = () => {
      if ((ref as RefObject<DynamicTableRef>)?.current) {
        ;(ref as RefObject<DynamicTableRef>).current?.refetch()
      }
    }

    if (variant === 'default')
      return (
        <>
          <DynamicDataTable<TData, TFilter>
            {...props}
            ref={ref}
            isSearchable={isSearchable}
            headerType={variant === 'default' ? 'full' : 'none'}
            permissions={{ delete: false }}
            limitControl={variant === 'default'}
            columns={columns}
          />
          <Modal
            actionButtonsOptions={{
              confirmText: 'Supprimer',
              cancelText: 'Fermer',
              onConfirm: async () => {
                if (!selectedData) return
                setLoading(true)
                try {
                  await restApiClient
                    .url(encodeNestedObject(props.endpoint, { ids: [selectedData.id] }))
                    .delete()
                  refetchTableData()
                  props.onDelete?.()
                } catch (error) {
                  console.error('Failed to delete data:', error)
                } finally {
                  setLoading(false)
                  toggleModal('delete')
                }
              }
            }}
            onCancel={() => toggleModal('delete')}
            isOpen={modalState.delete}
            title={
              selectedData
                ? getDeleteMessage<TData>([selectedData], { deleteMessage: props.deleteMessage })
                : 'Supprimer'
            }
            icon={DeleteIcon}
            isLoading={loading}
            variant={'destructive'}
          />
        </>
      )

    return (
      <ActionTableWrapper
        actionTableLabel={props.actionTableLabel}
        title={props.title || ''}
        actions={props.actions?.map((action, index) => (
          <Button
            key={index}
            type="button"
            icon={action.icon}
            label={action.label}
            variant={action.variant}
            onClick={() => action.fn?.([])}
          />
        ))}
      >
        <DynamicDataTable<TData, TFilter>
          {...props}
          ref={ref}
          isSearchable={false}
          headerType={'none'}
          permissions={{
            delete: false
          }}
          limitControl={props.limitControl ?? false}
          columns={columns}
        />
        <Modal
          actionButtonsOptions={{
            confirmText: t('dynamic.data.table.delete.label'),
            cancelText: t('dynamic.data.table.delete.cancel'),
            onConfirm: async () => {
              if (!selectedData) return
              setLoading(true)
              try {
                await restApiClient
                  .url(encodeNestedObject(props.endpoint, { ids: [selectedData.id] }))
                  .delete()
                refetchTableData()
                props.onDelete?.()
              } catch (error) {
                console.error('Failed to delete data:', error)
              } finally {
                setLoading(false)
                toggleModal('delete')
              }
            }
          }}
          onCancel={() => toggleModal('delete')}
          isOpen={modalState.delete}
          title={
            selectedData
              ? getDeleteMessage<TData>([selectedData], { deleteMessage: props.deleteMessage })
              : t('dynamic.data.table.delete.label')
          }
          icon={DeleteIcon}
          isLoading={loading}
          variant={'destructive'}
        />
      </ActionTableWrapper>
    )
  }
)

// Specify the generic type explicitly to satisfy TypeScript requirements
export default NestedDataTable as <
  TData,
  TFilter,
  TExpanded extends TableRequiredFields | undefined = undefined
>(
  props: Props<TData, TFilter, TExpanded> & { ref?: React.Ref<DynamicTableRef> }
) => JSX.Element
